import type { DeepReadonly, Ref } from 'vue'
import type { ScheduleSetting, ScheduleSettingWithCalendars } from '~/entities/meeting'

export type BookMeetingState = {
  status: 'active' | 'inactive'
  users: ScheduleSetting[]
  currentUser: ScheduleSettingWithCalendars
}

export type BookMeetingNoneAvailable = {
  status: 'none-available'
  currentUser: ScheduleSettingWithCalendars
}

export type BookMeetingIncomplete = {
  status: 'incomplete'
  users: ScheduleSetting[]
}

export type BookMeetingNotSetup = {
  status: 'not-setup'
}

export type BookMeetingStatus = BookMeetingState | BookMeetingNotSetup | BookMeetingNoneAvailable | BookMeetingIncomplete

export interface UseBookMeeting {
  requestSettings: DeepReadonly<Ref<BookMeetingStatus | null | undefined>>
  fetchRequestSettings(companyId: number): Promise<void>
  activateBookMeeting(currentUser: ScheduleSetting): Promise<void>
}

export function useBookMeeting(): UseBookMeeting {
  const state = reactive<{ requestSettings: BookMeetingStatus | null }>({
    requestSettings: null,
  })

  async function activateBookMeeting(currentUser: ScheduleSetting) {
    await $fetch(`v2/meetings/request-settings/${currentUser.id}`, {
      method: 'PATCH',
      body: { is_active: true },
      ...useFetchSharedOptions(),
    })
    await fetchRequestSettings(currentUser.company_id!)
  }

  async function fetchRequestSettings(companyId: number): Promise<void> {
    const [users, userSettings] = await Promise.all([
      $fetch<ScheduleSetting[]>(`v2/companies/${companyId}/request-settings`, {
        params: { include: ['able_to_book'] },
        ...useFetchSharedOptions(),
      }),
      _getCurrentUserSettings(companyId),
    ])
    _setMeetingStatus(users, userSettings)
  }

  async function _getCurrentUserSettings(companyId: number): Promise<ScheduleSettingWithCalendars | undefined> {
    try {
      const [requestSettings, cronofy] = await Promise.all([
        $fetch<ScheduleSetting[]>('v2/meetings/request-settings', { params: { include: ['able_to_book'] }, ...useFetchSharedOptions() }),
        $fetch<ScheduleSettingWithCalendars>('v2/cronofy/settings', useFetchSharedOptions()),
      ])
      const user = requestSettings.find(setting => !companyId || setting.company_id === companyId)

      return !user ? undefined : { ...user, number_of_calendars: cronofy.number_of_calendars }
    } catch (e) {
      return {} as ScheduleSettingWithCalendars
    }
  }

  function _setMeetingStatus(users: ScheduleSetting[], userSettings: ScheduleSettingWithCalendars | undefined): void {
    if (users.some((user: ScheduleSetting) => user.able_to_book)) {
      if (!userSettings?.id || userSettings.number_of_calendars === 0) {
        Object.assign(state, { requestSettings: { status: 'incomplete', users } })
        return
      }

      if (!userSettings.able_to_book) {
        Object.assign(state, { requestSettings: { status: 'inactive', users, currentUser: userSettings } })
      } else {
        Object.assign(state, { requestSettings: { status: 'active', users, currentUser: userSettings } })
      }

      return
    }

    if (!userSettings || !userSettings.id) {
      Object.assign(state, { requestSettings: { status: 'not-setup' } })
      return
    }

    Object.assign(state, { requestSettings: { status: 'none-available', currentUser: userSettings } })
  }

  return {
    ...toRefs(readonly(state)),
    fetchRequestSettings,
    activateBookMeeting,
  }
}

export function isBAMNotSetup(requestSettings: BookMeetingStatus): requestSettings is BookMeetingNotSetup {
  return requestSettings.status === 'not-setup'
}

export function isBAMSetupIncomplete(requestSettings: BookMeetingStatus): requestSettings is BookMeetingIncomplete {
  return requestSettings.status === 'incomplete'
}

export function isBAMUnavailable(requestSettings: BookMeetingStatus): requestSettings is BookMeetingNoneAvailable {
  return requestSettings.status === 'none-available'
}
